/**
 * =============================================================================
 * QUESTION DETAIL MODAL COMPONENT
 * =============================================================================
 *
 * Modal for viewing question details with image gallery.
 *
 * EXTRACTED FROM: QuestionQueue.tsx (P1-17)
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, Flex, FlexItem, Grid, GridItem, Text } from '@bigcommerce/big-design';
import { CheckIcon, CloseIcon } from '@bigcommerce/big-design-icons';
import { AccessibleModal } from '../common/AccessibleModal';
import { StatusBadge } from './StatusBadge';
import { QualityBadge } from './QualityBadge';
import { ReplyModerationPanel } from './ReplyModerationPanel';
import { RfiManagementPanel } from './Rfi/RfiManagementPanel';
import { AdminImageThumbnail } from '../Media/AdminImageThumbnail';
import { AdminImageUploader } from '../Media/AdminImageUploader';
import { MediaStatusBadge } from '../Media/MediaStatusBadge';
import { useQuestionMedia } from '../../hooks/useQuestionMedia';
import { formatDate } from '../../utils/formatters';
import { FILE_VALIDATION } from '@find-my-part/types';
import type { AdminQuestionListItem } from '@find-my-part/types';
import { tokens } from '../../tokens';
import {
  QuestionStatusWriteNotice,
  type QuestionStatusWriteIssue,
} from './QuestionStatusWriteNotice';

// =============================================================================
// P1-26: RESPONSIVE BREAKPOINT HELPER
// =============================================================================

const MOBILE_BREAKPOINT = 480;
const RESIZE_DEBOUNCE_MS = 150;

/**
 * Custom hook to detect mobile viewport for responsive adjustments.
 * Uses 480px as the mobile breakpoint per P1-26 specification.
 *
 * Includes debouncing (150ms) to prevent excessive re-renders during window resize.
 * P1-26 Review Fix: Added SSR safety guard and debounce per external review.
 */
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // P1-26 Review Fix: SSR safety guard
    if (typeof window === 'undefined') return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const checkMobile = (): void => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    // P1-26 Review Fix: Debounced handler to prevent excessive re-renders
    const handleResize = (): void => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkMobile, RESIZE_DEBOUNCE_MS);
    };

    // Check on mount
    checkMobile();

    // Listen for resize with debounce
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  return isMobile;
}

// The authenticated image thumbnail previously defined inline here was
// extracted to `components/Media/AdminImageThumbnail` (ADR-022 §10(C) —
// BL-IMG-ADMIN-MODERATION-001) so the media review queue and this modal share
// one implementation.

/**
 * Props for QuestionDetailModal component.
 */
export interface QuestionDetailModalProps {
  /** The question to display, or null if modal should be hidden */
  question: AdminQuestionListItem | null;
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Callback when approve button is clicked */
  onApprove: (questionId: string) => Promise<boolean | void>;
  /** Callback when reject button is clicked */
  onReject: (questionId: string) => Promise<void>;
  /** Auth token for image fetching */
  token: string;
  /** P1-8: Set of question IDs with pending actions (for disabling buttons) */
  pendingActions?: Set<string>;
  statusWriteIssue?: QuestionStatusWriteIssue | null;
  onStatusWriteReapply?: () => void;
  onStatusWriteRefresh?: () => void;
  onStatusWriteDismiss?: () => void;
  /**
   * A-0 fallback focus target, consulted only when the element focused before
   * open (the row's View button) is detached at close time — e.g. a confirmed
   * moderation action refreshed the list and re-created the rows. Point it at
   * a stable control (the queue's Refresh button) so focus never falls to
   * `<body>` (ADR-025 M11).
   */
  fallbackFocusTo?: React.RefObject<HTMLElement> | (() => HTMLElement | null);
  /** Optional test ID prefix */
  testId?: string;
}

/**
 * Question detail modal component.
 *
 * Displays full question details including:
 * - Title, status, poster info, date
 * - Quality score
 * - Full description
 * - Image gallery with lightbox
 * - Approve/reject actions for pending questions
 *
 * @example
 * ```tsx
 * <QuestionDetailModal
 *   question={selectedQuestion}
 *   isOpen={isModalOpen}
 *   onClose={handleClose}
 *   onApprove={handleApprove}
 *   onReject={handleReject}
 *   token={authToken}
 *   testId="queue"
 * />
 * ```
 */
export function QuestionDetailModal({
  question,
  isOpen,
  onClose,
  onApprove,
  onReject,
  token,
  pendingActions,
  statusWriteIssue = null,
  onStatusWriteReapply,
  onStatusWriteRefresh,
  onStatusWriteDismiss,
  fallbackFocusTo,
  testId,
}: QuestionDetailModalProps): React.ReactElement | null {
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const isPendingFromParent = question
    ? (pendingActions?.has(question.questionId) ?? false)
    : false;
  const isLoading = isApproving || isRejecting || isPendingFromParent;

  // The queue keeps this component MOUNTED across close (so A-0's focus
  // restore can fire on the open→closed transition — ext-R1 M11 retrofit);
  // clear any lightbox left open so a later re-open starts on the detail view.
  useEffect(() => {
    if (!isOpen) setSelectedImage(null);
  }, [isOpen]);

  // ext-W2-C2 dirty-close guard: the reply panel reports whether an open
  // rejection form carries a non-empty draft; every close route this modal
  // OWNS (its Esc / X / backdrop / action buttons) then asks the panel before
  // closing — the panel routes the request through the form's discard prompt
  // (M4 "prompts before discard"). The queue's close-on-confirmed-question-
  // reject deliberately bypasses this (completing the question-level action
  // is an explicit operator decision) — BL-ADMIN-REPLY-DRAFT-PARENT-CLOSE-001.
  const [replyDraftDirty, setReplyDraftDirty] = useState(false);
  const [closeRequestNonce, setCloseRequestNonce] = useState(0);
  useEffect(() => {
    // A dirty flag must never survive a close or a question switch.
    if (!isOpen) setReplyDraftDirty(false);
  }, [isOpen, question?.questionId]);
  const requestClose = useCallback((): void => {
    // Keep the mutation's owning surface mounted until its outcome is known.
    // Otherwise an Esc/X/backdrop click can hide the detail-origin conflict
    // that the queue publishes when the pending request eventually resolves.
    if (isLoading) return;
    if (replyDraftDirty) {
      setCloseRequestNonce((n) => n + 1);
      return;
    }
    onClose();
  }, [isLoading, replyDraftDirty, onClose]);
  const handleParentCloseResolve = useCallback(
    (proceed: boolean): void => {
      if (proceed && !isLoading) onClose();
    },
    [isLoading, onClose]
  );

  // P1-26: Detect mobile viewport for responsive layout
  const isMobile = useIsMobile();

  // PR4b (ADR-022 §10(A)/(B)): the modal receives a STALE `question.images`
  // snapshot from the queue row; the media hook fetches the LIVE rows (with
  // moderation status + legal hold) once open and re-fetches after every
  // upload/remove/hide/show, so the management section renders server truth.
  const {
    media,
    isMutating: isMediaMutating,
    uploadImage,
    removeImage,
    setVisibility,
  } = useQuestionMedia({
    questionId: question?.questionId ?? '',
    token,
    enabled: isOpen && question !== null && Boolean(token),
  });

  if (!question) return null;

  // P1-8: Include parent-level pending state to prevent concurrent actions
  const canModerate = question.status === 'PENDING_REVIEW' || isPendingFromParent;

  const handleApprove = async (): Promise<void> => {
    setIsApproving(true);
    try {
      const succeeded = await onApprove(question.questionId);
      if (succeeded !== false) requestClose();
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async (): Promise<void> => {
    setIsRejecting(true);
    try {
      // ADR-025 A-Q1: onReject now OPENS the required-reason dialog and
      // resolves immediately — it no longer performs the rejection. Do NOT
      // close this modal here: the queue closes it only on CONFIRMED success,
      // so Cancel returns the moderator to this exact context (peer-review F2).
      await onReject(question.questionId);
    } finally {
      setIsRejecting(false);
    }
  };

  // Build modal actions based on moderation capability
  const modalActions = canModerate
    ? [
        {
          text: 'Reject',
          variant: 'secondary' as const,
          actionType: 'destructive' as const,
          onClick: handleReject,
          isLoading: isRejecting,
          disabled: isLoading,
          iconLeft: <CloseIcon />,
        },
        {
          text: 'Approve',
          variant: 'primary' as const,
          onClick: handleApprove,
          isLoading: isApproving,
          disabled: isLoading,
          iconLeft: <CheckIcon />,
        },
      ]
    : [
        {
          text: 'Close',
          variant: 'secondary' as const,
          onClick: requestClose,
          disabled: isLoading,
        },
      ];

  return (
    <>
      {/* ADR-025 M11 (ext-R1): this file changed in A-Q1, so both dialogs it
          owns enter the hard-pass — AccessibleModal guarantees the accessible
          name, Esc, initial focus, and focus restore. */}
      <AccessibleModal
        isOpen={isOpen && !selectedImage}
        onClose={requestClose}
        closeOnEscKey={!isLoading}
        closeOnClickOutside={!isLoading}
        header="Question Details"
        actions={modalActions}
        fallbackFocusTo={fallbackFocusTo}
        data-testid={testId ? `${testId}-detail-modal` : undefined}
      >
        <QuestionStatusWriteNotice
          issue={statusWriteIssue}
          busy={isLoading}
          onReapply={onStatusWriteReapply}
          onRefresh={onStatusWriteRefresh}
          onDismiss={onStatusWriteDismiss}
          restoreFocusTo={() => {
            const dialog = document.querySelector<HTMLElement>('[role="dialog"]');
            if (!dialog) return null;
            const buttons = Array.from(dialog.querySelectorAll<HTMLButtonElement>('button'));
            return (
              buttons.find((button) => button.textContent?.trim() === 'Approve') ??
              buttons.find((button) => button.textContent?.trim() === 'Close') ??
              null
            );
          }}
          testId={testId ? `${testId}-detail-status-write` : 'detail-status-write'}
        />
        <Box marginBottom="medium">
          {/* P1-26: Single column on mobile, 2 columns on tablet+ */}
          <Grid gridColumns={isMobile ? '1fr' : '1fr 1fr'} gridGap="1rem">
            <GridItem>
              <Text bold>Title</Text>
              <Text>{question.title}</Text>
            </GridItem>
            <GridItem>
              <Text bold>Status</Text>
              <StatusBadge status={question.status} />
            </GridItem>
            <GridItem>
              <Text bold>Submitted By</Text>
              <Text>{question.posterName}</Text>
              <Text color="secondary">{question.posterEmail}</Text>
            </GridItem>
            <GridItem>
              <Text bold>Date</Text>
              <Text>{formatDate(question.createdAt)}</Text>
            </GridItem>
            <GridItem>
              <Text bold>Quality Score</Text>
              <QualityBadge qualityScore={question.qualityScore} />
            </GridItem>
          </Grid>
        </Box>

        <Box marginBottom="medium">
          <Text bold>Description</Text>
          <Box
            padding="small"
            backgroundColor="secondary10"
            style={{
              borderRadius: tokens.dimension.radius.sm,
              marginTop: tokens.dimension.space.sm,
            }}
          >
            <Text>{question.description || 'No description provided.'}</Text>
          </Box>
        </Box>

        {/* Images management section (PR4b — ADR-022 §10(A)/(B)). Until the
            live fetch lands, the stale snapshot renders read-only (it lacks
            moderation status / legal hold, so it can carry no manage actions);
            fetched rows carry Remove + Hide/Show. */}
        <Box data-testid={testId ? `${testId}-images` : undefined}>
          <Text bold marginBottom="small">
            Images ({media !== null ? media.length : (question.images?.length ?? 0)})
          </Text>
          {media !== null
            ? media.length > 0 && (
                <Flex flexGap={isMobile ? '0.5rem' : '0.75rem'} flexWrap="wrap">
                  {media.map((item) => (
                    <FlexItem key={item.attachmentId}>
                      <AdminImageThumbnail
                        src={item.url}
                        alt={item.originalFilename || 'Image attachment'}
                        token={token}
                        onClick={(blobUrl) => setSelectedImage(blobUrl)}
                        size={isMobile ? 80 : 120}
                      />
                      <Box marginTop="xxSmall">
                        <MediaStatusBadge status={item.moderationStatus} />
                      </Box>
                      {item.legalHold && (
                        <Text
                          color="secondary"
                          data-testid={
                            testId ? `${testId}-images-hold-${item.attachmentId}` : undefined
                          }
                        >
                          Legal hold
                        </Text>
                      )}
                      <Flex flexGap="0.25rem" marginTop="xxSmall" flexWrap="wrap">
                        {/* Hide/Show — the §10(B) guarded APPROVED ↔ HIDDEN pair.
                          SHOW is never offered on a held row (D-LEGALHOLD). */}
                        {item.moderationStatus === 'APPROVED' && (
                          <FlexItem>
                            <Button
                              variant="subtle"
                              disabled={isMediaMutating}
                              onClick={() => void setVisibility(item.attachmentId, 'HIDE')}
                              data-testid={
                                testId ? `${testId}-images-hide-${item.attachmentId}` : undefined
                              }
                            >
                              Hide
                            </Button>
                          </FlexItem>
                        )}
                        {item.moderationStatus === 'HIDDEN' && !item.legalHold && (
                          <FlexItem>
                            <Button
                              variant="subtle"
                              disabled={isMediaMutating}
                              onClick={() => void setVisibility(item.attachmentId, 'SHOW')}
                              data-testid={
                                testId ? `${testId}-images-show-${item.attachmentId}` : undefined
                              }
                            >
                              Show
                            </Button>
                          </FlexItem>
                        )}
                        <FlexItem>
                          <Button
                            variant="subtle"
                            actionType="destructive"
                            disabled={isMediaMutating || item.legalHold}
                            title={
                              item.legalHold
                                ? 'Legal hold: this image is preserved and cannot be removed.'
                                : undefined
                            }
                            aria-label={`Remove image ${item.originalFilename ?? item.attachmentId}`}
                            onClick={() => void removeImage(item.attachmentId)}
                            data-testid={
                              testId ? `${testId}-images-remove-${item.attachmentId}` : undefined
                            }
                          >
                            Remove
                          </Button>
                        </FlexItem>
                      </Flex>
                    </FlexItem>
                  ))}
                </Flex>
              )
            : question.images &&
              question.images.length > 0 && (
                /* P1-26: Adjust gap for mobile */
                <Flex flexGap={isMobile ? '0.5rem' : '0.75rem'} flexWrap="wrap">
                  {question.images.map((image) => (
                    <FlexItem key={image.attachmentId}>
                      <AdminImageThumbnail
                        src={image.url}
                        alt="Image attachment"
                        token={token}
                        onClick={(blobUrl) => setSelectedImage(blobUrl)}
                        size={isMobile ? 80 : 120}
                      />
                      <Text color="secondary">Attachment</Text>
                    </FlexItem>
                  ))}
                </Flex>
              )}

          {/* Staff question-management add (§10(A) "Add = upload"). Bounds are
              the FILE_VALIDATION platform defaults — a tenant-tightened §10.1
              cap still 400s server-side and surfaces as a per-file failure
              (client validation is UX only; the server is the gate). */}
          <Box marginTop="xSmall">
            <AdminImageUploader
              maxFiles={FILE_VALIDATION.MAX_IMAGES_PER_QUESTION}
              maxSizeMb={FILE_VALIDATION.MAX_SIZE_MB}
              allowedFormats={FILE_VALIDATION.ALLOWED_MIME_TYPES}
              onUpload={uploadImage}
              disabled={!token}
              testId={testId ? `${testId}-images-uploader` : undefined}
            />
          </Box>
        </Box>

        {/* GAP-008: Reply Moderation Section */}
        <Box
          marginTop="medium"
          paddingTop="medium"
          style={{ borderTop: `1px solid ${tokens.color.border.default}` }}
        >
          <ReplyModerationPanel
            questionId={question.questionId}
            questionStatus={question.status}
            token={token}
            onRejectDraftDirtyChange={setReplyDraftDirty}
            parentCloseRequestNonce={closeRequestNonce}
            onParentCloseResolve={handleParentCloseResolve}
            testId={testId ? `${testId}-replies` : undefined}
          />
        </Box>

        {/* PR G (BL-ADMIN-RFI-PANEL-001): RFI "Need-More-Info" management section */}
        <Box
          marginTop="medium"
          paddingTop="medium"
          style={{ borderTop: `1px solid ${tokens.color.border.default}` }}
        >
          <RfiManagementPanel
            questionId={question.questionId}
            questionStatus={question.status}
            token={token}
            testId={testId ? `${testId}-rfi` : undefined}
          />
        </Box>
      </AccessibleModal>

      {/* Image Lightbox Modal. The swap pattern (`isOpen && !selectedImage`
          above) remounts the detail dialog when this closes, and the remounted
          trap takes focus there — so no fallbackFocusTo here: a fallback would
          steal focus OUT of the reopened dialog. Overlay semantics land with
          B-4 (blueprint M10). */}
      <AccessibleModal
        isOpen={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        closeOnEscKey
        closeOnClickOutside
        header="Image Preview"
        actions={[{ text: 'Close', variant: 'secondary', onClick: () => setSelectedImage(null) }]}
      >
        {selectedImage && (
          <Box style={{ textAlign: 'center' }}>
            <img
              src={selectedImage}
              alt="Full size preview"
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                borderRadius: tokens.dimension.radius.sm,
              }}
            />
          </Box>
        )}
      </AccessibleModal>
    </>
  );
}
